import React from 'react';
import { createPortal } from 'react-dom';
import { fmt } from './oilApi';
import { OilIcon } from './OilIcons';
import { sfxClose } from './oilSounds';

const CHIP = {
  made: {
    SUCCESS_HIT: { label: 'ACERTOU', cls: 'bg-emerald-600 text-black' },
    SUCCESS_BLOCKED: { label: 'ESCUDO BLOQUEOU', cls: 'bg-sky-500 text-black' },
    FAILED: { label: 'FALHOU', cls: 'bg-zinc-700 text-zinc-300' },
  },
  taken: {
    SUCCESS_HIT: { label: 'VOCÊ FOI ATINGIDO', cls: 'bg-red-600 text-white' },
    SUCCESS_BLOCKED: { label: 'SEU ESCUDO SEGUROU', cls: 'bg-sky-500 text-black' },
    FAILED: { label: 'FALHOU', cls: 'bg-zinc-700 text-zinc-300' },
  },
};

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(/([+-]\d{2}:?\d{2}|Z)$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16).replace('T', ' ');
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const Row = ({ r, kind, services, onRevenge, busyId, truce, locked }) => {
  const chip = (CHIP[kind][r.status]) || CHIP[kind].FAILED;
  const spy = !r.offensive;
  const cost = (services || []).find((s) => s.id === r.service_id)?.cost_black_coins ?? r.cost;
  const running = busyId === r.attack_id;
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-2.5" data-testid={`oil-rixa-${r.attack_id}`}>
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-amber-700/60 bg-black">
          {r.avatar
            ? <img src={r.avatar} alt="" className="h-full w-full object-cover" />
            : <span className="flex h-full w-full items-center justify-center text-sm">🦊</span>}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[11px] font-black text-zinc-100">
            {kind === 'made' ? (spy ? 'Espionou ' : 'Atacou ') : 'Foi atacado por '}
            <span className="text-amber-300">{r.nickname}</span>
          </p>
          <p className="truncate text-[10px] text-zinc-400">{r.service} · {when(r.created_at)}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-black ${chip.cls}`}>
          {spy && r.status === 'SUCCESS_HIT' ? 'RELATÓRIO OBTIDO' : chip.label}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] font-bold">
        {r.damage > 0 && (
          <span className={kind === 'made' ? 'text-emerald-400' : 'text-red-400'}>
            {kind === 'made' ? 'Dano causado' : 'Prejuízo'}: {fmt(r.damage)} FOX Dólar
          </span>
        )}
        {kind === 'made' && r.respect > 0 && <span className="text-rose-300">+{fmt(r.respect)} respeito</span>}
        {kind === 'made' && r.cost > 0 && <span className="text-zinc-500">custou {fmt(r.cost)} Black Coins</span>}
      </div>

      {kind === 'taken' && (
        r.revenged ? (
          <p className="mt-2 rounded-lg border border-emerald-800/70 bg-emerald-950/40 py-1.5 text-center text-[9px] font-black uppercase tracking-wider text-emerald-300"
            data-testid={`oil-revenge-done-${r.attack_id}`}>
            ⚔ Revanche já enviada
          </p>
        ) : (
          <button onClick={() => onRevenge?.(r)} disabled={!!busyId || truce || locked}
            data-testid={`oil-revenge-${r.attack_id}`}
            className={`mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-[10px] font-black uppercase tracking-wider transition-transform active:scale-[0.98] ${
              truce || locked
                ? 'bg-zinc-800 text-zinc-500'
                : 'bg-gradient-to-r from-rose-600 to-rose-800 text-white shadow-lg shadow-rose-950/60 disabled:opacity-60'}`}>
            {running ? 'REVIDANDO...'
              : truce ? 'TRÉGUA ATIVA'
              : locked ? 'LIMITE DIÁRIO ATINGIDO'
              : <>⚔ Revidar com {r.service} · <OilIcon k="black" /> {fmt(cost)}</>}
          </button>
        )
      )}
    </div>
  );
};

export const OilRixas = ({ data, tab, onTab, onClose, services, onRevenge, revengeId, truce }) => {
  const rows = tab === 'made' ? (data?.made || []) : (data?.taken || []);
  const used = data?.attacks_today || 0;
  const cap = data?.max_attacks_per_day || 0;
  const locked = cap > 0 && used >= cap;

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      data-testid="oil-rixas-overlay" onClick={onClose}>
      <div className="w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-[2px]" style={{ background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)', borderRadius: '18px' }}>
          <div className="overflow-hidden rounded-[16px] bg-[#0b0906] p-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-amber-300">
                <img src="/oil/ic_rixas.webp" alt="" className="h-7 w-7 object-contain" /> Rixas
              </p>
              <div className="flex items-center gap-2">
                {cap > 0 && (
                  <span data-testid="oil-rixas-daily-ops"
                    className={`rounded-full border px-2 py-1 text-[9px] font-black ${locked
                      ? 'border-red-800 bg-red-950/60 text-red-300'
                      : 'border-zinc-700 bg-black/60 text-zinc-300'}`}>
                    {used}/{cap} OPS HOJE
                  </span>
                )}
                <button onClick={() => { sfxClose(); onClose(); }} data-testid="oil-rixas-close"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-sm font-black text-amber-300">✕</button>
              </div>
            </div>

            <div className="mb-2.5 flex gap-1 rounded-lg border border-zinc-800 bg-black/60 p-1">
              {[
                { id: 'made', label: `EU FIZ (${(data?.made || []).length})` },
                { id: 'taken', label: `EU SOFRI (${(data?.taken || []).length})` },
              ].map((t) => (
                <button key={t.id} onClick={() => onTab(t.id)} data-testid={`oil-rixas-tab-${t.id}`}
                  className={`flex-1 rounded-md py-1.5 text-[10px] font-black tracking-wider transition-colors ${
                    tab === t.id ? 'bg-gradient-to-b from-amber-400 to-amber-700 text-black' : 'text-zinc-400'}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {rows.length === 0 ? (
              <p className="py-6 text-center text-xs text-zinc-500" data-testid="oil-rixas-empty">
                {tab === 'made'
                  ? 'Você ainda não contratou nenhum serviço sujo.'
                  : 'Ninguém encostou no seu império até agora.'}
              </p>
            ) : (
              <div className="max-h-[55vh] space-y-1.5 overflow-y-auto" data-testid="oil-rixas-list">
                {rows.map((r) => (
                  <Row key={r.attack_id} r={r} kind={tab} services={services}
                    onRevenge={onRevenge} busyId={revengeId} truce={truce} locked={locked} />
                ))}
              </div>
            )}
            {tab === 'taken' && rows.length > 0 && (
              <p className="mt-2 text-center text-[9px] text-zinc-500">
                A revanche usa o mesmo serviço que te acertou. Custa Black Coins e conta no seu limite diário.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OilRixas;
